import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { FoundationLogo } from "@/components/FoundationLogo";
import { activeProduct } from "@/lib/productBrand";
import { activeMarket } from "@/lib/marketBoot";
import { FOUNDATION_COMPANY, companyDisclosureLines } from "@/lib/foundationCompany";
import { C, T, L, M, label } from "@/lib/foundationTheme";
import { scaleRule, Cross } from "@/components/foundation/Brand";

/**
 * Foundation marketing chrome — always the Foundation brand, never the tenant's.
 *
 * Direction: "Title Plan". The chrome is drawing-sheet furniture. The header's
 * bottom edge is a map scale bar, the active nav item is the red boundary line
 * reduced to a single edge, and the footer is the sheet's reverse — an ink
 * panel carrying contour linework.
 *
 * This layout is intentionally distinct from the tenant SiteHeader. Every
 * colour comes from lib/foundationTheme; nothing here declares a hex.
 */

// This chrome now wraps only the legal documents, checkout and 404 — the
// marketing pages it was built for live at getstatehouse.com, in their own
// repo. Every entry here used to point at one of those pages, so the nav was
// four links to nothing. Product and Pricing leave the app deliberately;
// Sign in is the one thing a reader of these pages actually wants next.
const navItems = () => [
  { label: "Product", href: activeProduct().pricingUrl },
];

/** Scroll a `#section` target into view — react-router never does this itself. */
function scrollToHash(hash) {
  const el = document.getElementById(hash.replace("#", ""));
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Lands the page on its hash target after the app's ScrollToTop has had its
 * say — that sits above the route in the tree, so its effect runs first and
 * this one gets the last word on where the page settles.
 */
function useHashScroll() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) scrollToHash(hash);
  }, [pathname, hash]);
}

/**
 * Every marketing link is an in-app navigation, never a page load — a full
 * reload tears the SPA down, re-downloads the bundle, flashes white and
 * loses the reader's place, on a site whose whole promise is that it feels
 * considered.
 *
 * The one hash target, `/#products`, is navigated with its hash intact so
 * the section is scrolled to by useHashScroll and the nav can tell it apart
 * from a plain visit to the home page.
 */
function MarketingLink({ href, onNavigate, children, ...rest }) {
  const location = useLocation();
  // An absolute URL is a different site — Statehouse's marketing pages are
  // not in this app — and react-router's Link would treat it as a path.
  if (/^https?:\/\//.test(href)) {
    return (
      <a href={href} target="_blank" rel="noreferrer" onClick={onNavigate} {...rest}>
        {children}
      </a>
    );
  }
  const [path, hash] = href.split("#");
  if (!hash) {
    return (
      <Link to={href} onClick={onNavigate} {...rest}>
        {children}
      </Link>
    );
  }
  const target = { pathname: path || "/", hash: `#${hash}` };
  return (
    <Link
      to={target}
      onClick={(e) => {
        onNavigate?.();
        // Already on that hash: the location wouldn't change, so nothing
        // would fire the scroll — do it here instead.
        if (location.pathname === target.pathname && location.hash === target.hash) {
          e.preventDefault();
          scrollToHash(target.hash);
        }
      }}
      {...rest}
    >
      {children}
    </Link>
  );
}

/** Nav item. Current page carries the red line reduced to one edge. */
function NavItem({ href, current, children, ...rest }) {
  const [hover, setHover] = useState(false);
  return (
    <MarketingLink
      href={href}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        fontFamily: T.body,
        fontWeight: 500,
        fontSize: 15,
        color: current || hover ? C.ink : C.secondary,
        position: "relative",
        paddingBottom: 22,
        transition: `color ${M.hover} ${M.ease}`,
      }}
      {...rest}
    >
      {children}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          height: 2,
          width: current ? "100%" : hover ? "100%" : 0,
          background: C.red,
          transition: `width ${M.hover} ${M.ease}`,
        }}
      />
    </MarketingLink>
  );
}

export function FoundationHeader() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // A bare pathname comparison never matches the hash target `/#products`,
  // so Products could never be the current page.
  const isCurrent = (href) =>
    href.includes("#") ? `${location.pathname}${location.hash}` === href : location.pathname === href;

  return (
    <header
      className="sticky top-0 z-40"
      style={{ background: "rgba(242, 239, 231, 0.94)", backdropFilter: "blur(8px)", ...scaleRule }}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 md:h-[72px] flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center shrink-0" data-testid="foundation-logo-link">
          <FoundationLogo size={26} tone="light" data-testid="foundation-logo" />
        </Link>

        <nav className="hidden md:flex items-center gap-9 self-stretch pt-[26px]">
          {navItems().map((n) => (
            <NavItem
              key={n.href}
              href={n.href}
              current={isCurrent(n.href)}
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavItem>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {/* Both labels are mono caps with wide tracking, so they need
              nowrap: at tablet widths "Sign in" and "Get started" each broke
              onto two lines and pushed the header row out of alignment. */}
          <Link
            to="/admin/login"
            className="hidden sm:block"
            style={{ ...label(11), color: C.secondary, padding: "8px 4px", whiteSpace: "nowrap" }}
            data-testid="nav-signin"
          >
            Sign in
          </Link>
          <a
            href={activeProduct().pricingUrl}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex items-center"
            style={{
              ...label(12),
              background: C.ink,
              color: C.paper,
              border: `1px solid ${C.ink}`,
              borderRadius: L.radius,
              padding: "11px 20px",
              whiteSpace: "nowrap",
            }}
            data-testid="nav-get-started"
          >
            Get started
          </a>
          <button
            type="button"
            className="md:hidden flex items-center justify-center w-10 h-10 -mr-2"
            style={{ color: C.ink }}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          className="md:hidden px-6 py-2 flex flex-col"
          style={{ background: C.paper, borderTop: `1px solid ${C.hairline}` }}
          data-testid="nav-mobile-menu"
        >
          {navItems().map((n, i) => (
            <MarketingLink
              key={n.href}
              href={n.href}
              onNavigate={() => setMobileOpen(false)}
              className="flex items-baseline gap-4 py-3.5"
              style={{
                color: isCurrent(n.href) ? C.ink : C.secondary,
                borderBottom: `1px solid ${C.hairline}`,
                fontFamily: T.body,
                fontWeight: 500,
                fontSize: 17,
              }}
              data-testid={`nav-mobile-${n.label.toLowerCase()}`}
            >
              {/* The ledger index, carried into the mobile menu so it reads as
                  part of the same document rather than a stock drawer. */}
              <span style={{ ...label(10), color: C.secondary }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              {n.label}
            </MarketingLink>
          ))}
          <Link
            to="/admin/login"
            onClick={() => setMobileOpen(false)}
            className="py-3.5"
            style={{ ...label(12), color: C.secondary, borderBottom: `1px solid ${C.hairline}` }}
            data-testid="nav-mobile-signin"
          >
            Sign in
          </Link>
          <a
            href={activeProduct().pricingUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => setMobileOpen(false)}
            className="mt-5 mb-3 text-center"
            style={{
              ...label(12),
              background: C.ink,
              color: C.paper,
              border: `1px solid ${C.ink}`,
              borderRadius: L.radius,
              padding: "14px 20px",
            }}
            data-testid="nav-mobile-get-started"
          >
            Get started
          </a>
        </nav>
      )}
    </header>
  );
}

export function FoundationFooter() {
  const isUS = activeMarket() === "us";
  return (
    <footer
      className="mt-32"
      style={{ background: C.panel, color: C.paper, position: "relative", overflow: "hidden" }}
    >
      {/* Contour linework — the reverse of the survey sheet. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1200 600"
        preserveAspectRatio="xMidYMid slice"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.07 }}
      >
        <g fill="none" stroke={C.paper} strokeWidth="1">
          <path d="M-60 420C160 380 300 300 470 318s250 120 430 92 300-120 420-96" />
          <path d="M-60 470C170 428 320 348 500 366s240 122 420 94 300-122 400-98" />
          <path d="M-60 520C180 476 340 396 530 414s230 124 410 96 300-124 380-100" />
          <path d="M-40 130C130 96 260 60 400 74s210 78 350 62 240-64 350-50" />
          <path d="M-40 182C140 146 280 108 420 122s200 80 340 64 250-66 360-52" />
        </g>
      </svg>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-24 pb-14" style={{ position: "relative" }}>
        <div
          className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr] pb-16"
          style={{ borderBottom: `1px solid ${C.hairlineDark}` }}
        >
          <div>
            <FoundationLogo orientation="stacked" tone="dark" size={40} data-testid="foundation-logo-footer" />
            <p
              className="mt-8 max-w-xs"
              style={{ fontFamily: T.body, fontSize: 15, lineHeight: 1.65, color: C.mutedDark }}
            >
              {activeMarket() === "us"
                ? "A real estate CRM that knows your state. Escrow milestones, disclosures, option periods and closing rules, resolved for all 50 states and DC."
                : "Everything your property business is built on. One platform for CRM, valuations, websites, marketing and reporting."}
            </p>
            {/* How to reach Foundation. There was previously no email, phone or
                company number anywhere on the marketing site — a prospect with
                one question before buying had nowhere to send it, and the
                trading disclosure a UK company owes its visitors was missing. */}
            <div className="mt-6 space-y-2">
              <a
                href={`mailto:${activeProduct().supportEmail}`}
                style={{
                  ...label(11),
                  color: C.paper,
                  borderBottom: `1px solid ${C.signal}`,
                  paddingBottom: 2,
                }}
                data-testid="footer-contact-email"
              >
                {activeProduct().supportEmail}
              </a>
              {/* The phone number is Foundation's UK line. Statehouse has
                  none to print yet, and a British number under an American
                  product is worse than no number. */}
              {!isUS && FOUNDATION_COMPANY.phone && (
                <div style={{ ...label(11), color: C.mutedDark }}>{FOUNDATION_COMPANY.phone}</div>
              )}
            </div>
          </div>
          <FooterCol title="Product" links={[
            {label: `${activeProduct().name}`, href: activeProduct().pricingUrl},
            {label: "Sign in", href: "/admin/login"},
          ]} />
          <FooterCol title="Legal" links={[
            {label: "Privacy", href: "/legal/privacy"},
            {label: "Terms", href: "/legal/terms"},
            {label: "Cookies", href: "/legal/cookies"},
          ]} />
        </div>

        <div
          className="pt-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
          style={{ ...label(11), fontWeight: 400, letterSpacing: "0.08em", color: "rgba(242,239,231,0.5)" }}
        >
          <div className="space-y-1.5" data-testid="footer-company">
            <div>© {new Date().getFullYear()} {activeProduct().name}</div>
            {/* The trading disclosure a UK company owes its visitors:
                registered name, number and office (Companies Act 2006 s.82).
                It names Software Builders Ltd in England & Wales, which is
                NOT who an American brokerage would be contracting with, so
                printing it under Statehouse would be a false statement about
                the counterparty rather than a missing nicety. Statehouse has
                no US entity on file yet; the gap is deliberate and is the
                same open item as its legal pages. */}
            {!isUS && companyDisclosureLines().map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
          {/* The grid reference: the sheet signing off with where it was drawn. */}
          <div className="flex items-center gap-3">
            <Cross size={6} color={C.paper} style={{ opacity: 0.4 }} />
            <span>Surrey, England — 51.36°N 0.65°W</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div style={{ ...label(11), color: "rgba(242,239,231,0.5)", marginBottom: 20 }}>{title}</div>
      <div className="space-y-3">
        {links.map((l) => (
          <FooterLink key={l.href} href={l.href}>{l.label}</FooterLink>
        ))}
      </div>
    </div>
  );
}

function FooterLink({ href, children }) {
  const [hover, setHover] = useState(false);
  return (
    <MarketingLink
      href={href}
      className="block"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        fontFamily: T.body,
        fontWeight: 400,
        fontSize: 15,
        color: hover ? C.paper : C.mutedDark,
        transition: `color ${M.hover} ${M.ease}`,
      }}
    >
      {children}
    </MarketingLink>
  );
}

export default function FoundationLayout({ children }) {
  useHashScroll();
  return (
    // `.fdn` scopes the marketing typography — see index.css. The background is
    // set here AND on <body> (index.html) so overscroll doesn't reveal the
    // tenant-themed shadcn ground beneath.
    <div className="fdn min-h-screen" style={{ background: C.paper, color: C.ink }}>
      <FoundationHeader />
      {children}
      <FoundationFooter />
    </div>
  );
}
